import {
  applyTableLeafGetDataToFields,
  mergeSelectionOnlyCheckboxMetas,
  resolveTableLeafMetasFromElements,
  type TableLeafFieldMeta,
} from '@/common/utils/tableLeafGetData'

export type SynthCollectTableSelection = {
  fieldName: string
  rows?: readonly unknown[] | null
}

/**
 * Swing `putFormData` / `putData` с синтетическим Action (parent = Form):
 * checkbox Table → только checked rows (`addAll=false`), не полный store.
 *
 * @see dev/contracts/putFormData.md §3, §7.3
 */
export function applySynthActionCheckboxTableCollect(
  fields: Record<string, unknown>,
  selections: readonly SynthCollectTableSelection[] | null | undefined,
): Record<string, unknown> {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return fields
  }
  if (!selections?.length) {
    return fields
  }

  const metas = mergeSelectionOnlyCheckboxMetas([], selections)
  return applyTableLeafGetDataToFields(fields, metas, { mode: 'external' })
}

/**
 * Synth putFormData / начальный currentData кнопки: leaf getData таблиц
 * (checkbox TABLE/EDITABLE/GROUPTABLE / EDITABLE all / TABLE omit / TREETABLE checked).
 */
export function applySynthActionTableLeafCollect(
  fields: Record<string, unknown>,
  input: {
    elements?: Record<string, unknown> | null
    extraMaps?: Array<Record<string, unknown> | null | undefined>
    formId?: string | number | null
    selections?: readonly SynthCollectTableSelection[] | null
    shouldRewriteField?: (meta: TableLeafFieldMeta) => boolean
    shellVisibleByElementId?: Record<string, boolean> | { value?: Record<string, boolean> } | null
  },
): Record<string, unknown> {
  const metas = mergeSelectionOnlyCheckboxMetas(
    resolveTableLeafMetasFromElements(
      input.elements,
      input.formId,
      input.extraMaps,
      input.shellVisibleByElementId,
    ),
    input.selections,
  )
  return applyTableLeafGetDataToFields(fields, metas, {
    mode: 'external',
    shouldRewriteField: input.shouldRewriteField,
    elements: input.elements,
    extraMaps: input.extraMaps,
  })
}

/** Собрать selection-slice для form-wide synth collect (En / JSContext). */
export function resolveFormTableSelectionsForSynthCollect(input: {
  formId?: string | number | null
  getTableSelections?: (
    formId?: string | number,
    panelId?: string | number,
  ) => Array<{ fieldName: string; rows: unknown[] }> | null | undefined
}): SynthCollectTableSelection[] {
  const formId = input.formId
  if (formId == null || formId === '') return []
  const getter = input.getTableSelections
  if (typeof getter !== 'function') return []
  const list = getter(formId)
  if (!Array.isArray(list) || list.length === 0) return []

  const byField = new Map<string, SynthCollectTableSelection>()
  for (const sel of list) {
    const fieldName = String(sel?.fieldName ?? '').trim()
    if (!fieldName) continue
    // Последняя запись по fieldName побеждает (несколько panel keys в Map).
    byField.set(fieldName, {
      fieldName,
      rows: Array.isArray(sel.rows) ? sel.rows : [],
    })
  }
  return Array.from(byField.values())
}
